'use client';

import { useState, useEffect } from 'react';
import { RatingInteraction } from '@/components/RatingInteraction';
import Image from 'next/image';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';


interface VoteItem {
  id: string;
  imageUrl: string;
  score: number;
  popularityScore: number;
  likesCount: number;
  selectedTags: string[];
  commentsCount: number;
  hasUserLiked: boolean;
}

const commentTags = {
  appearance: [
    "Smile is infectious!",
    "Facial features are harmonious!",
    "Skin looks great!"
  ],
  personality: [
    "Photo shows unique style!",
    "Rating seems realistic.",
    "Confidence shines through!"
  ],
  photoQuality: [
    "Better lighting would enhance it.",
    "Simpler background would focus more.",
    "Different angle could highlight strengths."
  ],
  reliability: [
    "Rating matches my intuition.",
    "Results seem scientific and reliable.",
    "Subjective but generally accurate."
  ]
};

export default function VoteClient() {
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<VoteItem[]>([
    {
      id: '1',
      imageUrl: '/guides/time-1.jpg',
      score: 8.5,
      selectedTags: [],
      popularityScore: 8.7,
      likesCount: 245,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '2',
      imageUrl: '/guides/time-2.jpg',
      score: 8.2,
      selectedTags: [],
      popularityScore: 8.4,
      likesCount: 189,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '3',
      imageUrl: '/guides/time-3.jpg',
      score: 7.9,
      selectedTags: [],
      popularityScore: 8.1,
      likesCount: 178,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '4',
      imageUrl: '/guides/time-4.jpg',
      score: 8.1,
      selectedTags: [],
      popularityScore: 8.3,
      likesCount: 201,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '5',
      imageUrl: '/guides/time-5.jpg',
      score: 8.4,
      selectedTags: [],
      popularityScore: 8.6,
      likesCount: 234,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '6',
      imageUrl: '/guides/time-6.jpg',
      score: 7.8,
      selectedTags: [],
      popularityScore: 8.0,
      likesCount: 167,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '7',
      imageUrl: '/guides/time-7.jpg',
      score: 8.3,
      selectedTags: [],
      popularityScore: 8.5,
      likesCount: 212,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '8',
      imageUrl: '/guides/time-8.jpg',
      score: 8.0,
      selectedTags: [],
      popularityScore: 8.2,
      likesCount: 190,
      commentsCount: 0,
      hasUserLiked: false
    },
    {
      id: '9',
      imageUrl: '/guides/time-9.jpg',
      score: 8.6,
      selectedTags: [],
      popularityScore: 8.8,
      likesCount: 256,
      commentsCount: 0,
      hasUserLiked: false
    }
  ]);
  const [selectedItem, setSelectedItem] = useState<VoteItem>(items[0]);

  useEffect(() => {
    const fetchVoteData = async () => {
      try {
        const promises = items.map(async (item) => {
          const response = await fetch(`/api/vote?imageId=${item.id}`);
          if (!response.ok) throw new Error('Failed to fetch vote data');
          const data = await response.json();
          return {
            ...item,
            likesCount: data.likes,
            commentsCount: data.commentsCount || 0
          };
        });

        const updatedItems = await Promise.all(promises);
        setItems(updatedItems);
        setSelectedItem(updatedItems[0]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching vote data:', error);
        setLoading(false);
      }
    };

    fetchVoteData();
  }, []);

  const handleLike = async (itemId: string) => {
    try {
      const response = await fetch('/api/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageId: itemId,
          action: selectedItem.hasUserLiked ? 'unlike' : 'like',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update like status');
      }

      const { likes } = await response.json();

      setItems(items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            hasUserLiked: !item.hasUserLiked,
            likesCount: likes
          };
        }
        return item;
      }));
      setSelectedItem({
        ...selectedItem,
        hasUserLiked: !selectedItem.hasUserLiked,
        likesCount: likes
      });
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">
          Vote & Share
        </h1>
        <p className="text-xl text-gray-600 mb-6 text-center">
          Browse and vote on shared beauty analysis results
        </p>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto mb-4 relative">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden p-3">
              <div className="relative aspect-[16/10] w-full bg-white border border-gray-100 rounded-lg">
                <div className="absolute inset-0 bg-white" />
                <Image
                  src={selectedItem.imageUrl}
                  alt={`Beauty Score: ${selectedItem.score}`}
                  fill
                  className="object-contain z-10"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  priority
                  quality={100}
                />
              </div>
              <div className="mt-2 p-3 bg-white rounded-lg shadow-sm flex items-center justify-end">
                <div className="flex items-center space-x-6">
                  <button
                    onClick={() => handleLike(selectedItem.id)}
                    className="flex items-center space-x-2 transition-colors duration-200"
                  >
                    <svg
                      className={`w-5 h-5 ${selectedItem.hasUserLiked ? 'text-pink-500' : 'text-pink-500'}`}
                      fill={selectedItem.hasUserLiked ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                    <span className={`${selectedItem.hasUserLiked ? 'text-pink-500' : 'text-pink-500'}`}>
                      {selectedItem.likesCount}
                    </span>
                  </button>
                  <button
                    onClick={() => setShowComments(!showComments)}
                    className="flex items-center space-x-2 transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <span className="text-green-500">
                      {selectedItem.commentsCount}
                    </span>
                  </button>
                </div>
              </div>
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4">
                <button
                  onClick={() => {
                    const currentIndex = items.findIndex(item => item.id === selectedItem.id);
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                    setSelectedItem(items[prevIndex]);
                    setShowComments(false);
                  }}
                  className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
              <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4">
                <button
                  onClick={() => {
                    const currentIndex = items.findIndex(item => item.id === selectedItem.id);
                    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                    setSelectedItem(items[nextIndex]);
                    setShowComments(false);
                  }}
                  className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {showComments && (
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Add a Comment</h3>
                <p className="text-gray-600 mb-4">Select tags that best describe your thoughts</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(commentTags).map(([category, tags]) => (
                    <div key={category} className="space-y-2">
                      <h4 className="font-medium text-gray-700 capitalize">{category}</h4>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => {
                              const newSelectedTags = selectedItem.selectedTags.includes(tag)
                                ? selectedItem.selectedTags.filter((t) => t !== tag)
                                : [...selectedItem.selectedTags, tag];
                              setSelectedItem({
                                ...selectedItem,
                                selectedTags: newSelectedTags,
                              });
                            }}
                            className={`px-3 py-1 rounded-full text-sm ${selectedItem.selectedTags.includes(tag)
                              ? 'bg-violet-100 text-violet-700 border-2 border-violet-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'}`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={async () => {
                  if (selectedItem.selectedTags.length === 0) {
                    alert('请至少选择一个标签');
                    return;
                  }
                  try {
                    const response = await fetch('/api/vote', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        imageId: selectedItem.id,
                        action: 'comment',
                        tags: selectedItem.selectedTags
                      }),
                    });

                    if (!response.ok) {
                      throw new Error('Failed to update comments');
                    }

                    const { commentsCount } = await response.json();
                    
                    // 更新所有图片的状态
                    setItems(prevItems => prevItems.map(item => {
                      if (item.id === selectedItem.id) {
                        return {
                          ...item,
                          commentsCount,
                          selectedTags: []
                        };
                      }
                      return item;
                    }));
                    
                    // 更新当前选中图片的状态
                    setSelectedItem(prev => ({
                      ...prev,
                      commentsCount,
                      selectedTags: []
                    }));
                    
                    // 关闭评论面板
                    setShowComments(false);
                    
                    // 自动切换到下一张图片
                    const currentIndex = items.findIndex(item => item.id === selectedItem.id);
                    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                    setSelectedItem(items[nextIndex]);
                  } catch (error) {
                    console.error('Error adding comment:', error);
                    alert('添加评论失败，请稍后重试');
                  }
                }}
                className="mt-6 mx-auto py-2 px-8 rounded-lg text-white bg-violet-600 hover:bg-violet-700 transition-colors block"
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 